import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import { Card } from 'lucid-components-react';
import React, { useContext } from 'react';

import Link from '../link/link';
import LayoutContext from '../../contexts/layoutContext';
import { Drupal_ParagraphFullCard } from '../../generated/graphql';
import { getProcessedValue } from '../../utils/helpers';

import './tiles.scss';

interface Props {
  tiles: Drupal_ParagraphFullCard[];
  bgColor?: string;
}

const Tiles = ({ tiles, bgColor }: Props) => {
  const filteredTiles = tiles.filter(
    tile => tile.fieldCtaHeading && tile.fieldLink
  );

  const tileGroups = filteredTiles
    .map((_, i) => (i % 4 === 0 ? filteredTiles.slice(i, i + 4) : null))
    .filter(i => i);

  const {
    language: { i18nKey }
  } = useContext(LayoutContext);

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_learn_more
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  if (filteredTiles.length === 0) {
    return null;
  }

  return (
    <section
      className="tiles-component"
      style={
        bgColor
          ? {
              backgroundColor: bgColor
            }
          : {}
      }
    >
      <div className="lucid-container tiles">
        {tileGroups.map((group, i) => {
          const flip = i % 2;
          const bigTile = group?.find(tile => tile);
          const remainingTiles = group?.slice(1);
          return (
            <div className="tiles__row" key={`row-${i}`}>
              <div
                className={`container--half tiles__group tiles__group--single ${
                  flip ? 'tiles__group--flip' : ''
                }`}
              >
                <Card
                  isInteractive
                  className="tiles__tile tiles__tile--lg tiles__tile--full-mobile"
                >
                  <Link
                    to={bigTile?.fieldLink || ''}
                    underline="none"
                    className="tiles__tile-link tiles__tile-link--lg"
                  >
                    {bigTile?.fieldImage && (
                      <Card.Image className="tiles__tile-image-wrap">
                        <img
                          className="tiles__tile-image"
                          src={bigTile?.fieldImage}
                          alt={bigTile?.fieldCtaHeading!}
                        />
                      </Card.Image>
                    )}

                    <Card.Content className="tiles__tile-content">
                      <h2 className="tiles__tile-title">
                        {bigTile?.fieldCtaHeading}
                      </h2>
                      {bigTile?.fieldCardSubheader && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: getProcessedValue(
                              bigTile?.fieldCardSubheader
                            )
                          }}
                          className="tiles__tile-description"
                        />
                      )}
                      <p className="tiles__more-link">
                        {i18n('mktg-learn-more')}
                      </p>
                    </Card.Content>
                  </Link>
                </Card>
              </div>

              {remainingTiles && (
                <div className="container--half layout__item--12-tablet tiles__group">
                  {remainingTiles?.map((tile, i) => (
                    <Card
                      key={tile.fieldCtaHeading || `tile-${i}`}
                      isInteractive
                      className={`tiles__tile tiles__tile--${
                        i === 0 ? 'md' : 'sm'
                      }  tiles__tile--full-mobile`}
                    >
                      <Link
                        to={tile.fieldLink || ''}
                        underline="none"
                        className={`tiles__tile-link tiles__tile-link--${
                          i === 0 ? 'md' : 'sm'
                        }  tiles__tile--full-mobile`}
                      >
                        <Card.Image className="tiles__tile-image-wrap">
                          <img
                            className="tiles__tile-image"
                            src={tile.fieldImage!}
                            alt={tile.fieldCtaHeading!}
                          />
                        </Card.Image>

                        <Card.Content className="tiles__tile-content">
                          <h2 className="tiles__tile-title">
                            {tile.fieldCtaHeading}
                          </h2>

                          {tile.fieldCardSubheader && i < 1 && (
                            <div>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: getProcessedValue(
                                    tile.fieldCardSubheader
                                  )
                                }}
                                className="tiles__tile-description"
                              />
                              <p className="tiles__more-link">
                                {i18n('mktg-learn-more')}
                              </p>
                            </div>
                          )}
                        </Card.Content>
                      </Link>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Tiles;
